import { Box, Column, Pagination, Table } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { AppliedDataFilter, ProgramType } from 'types';

import {
  useGetPriceSheetMappingsListFilterQuery,
  useGetPriceSheetMappingsListFiltersQuery,
  useLazyGetPriceSheetMappingsQuery,
} from '../../../services/pricing';
import { FilterBar } from '../../filters';
import { AddFilterButtonDropdown } from '../../filters/AddFilterButton';
import type { UseFilterById } from '../../filters/FilterButton';

import './mappings.css';
import { TableLoading } from '../../TableLoading';

interface FilteredMappingsListProps {
  columnConfig: Column[];
}

const PAGE_SIZE = 20;

// Stable hook factory bound to `programType`. Returning a memoized `UseFilterById` keeps
// the prop reference stable across renders so downstream memoization (if any) is preserved.
function usePriceSheetMappingsFilterById(programType: ProgramType): UseFilterById {
  return useCallback(
    (id) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data, isLoading } = useGetPriceSheetMappingsListFilterQuery({ programType, id }, { skip: !id });
      return { data, isLoading };
    },
    [programType],
  );
}

export const FilteredMappingsList = ({ columnConfig }: FilteredMappingsListProps) => {
  const { programType = ProgramType.solar } = useParams<{ programType: string }>();
  const [trigger, { data: mappingResponse, isLoading, isFetching }] = useLazyGetPriceSheetMappingsQuery({});
  const { data: filters = [], isLoading: isFiltersLoading } = useGetPriceSheetMappingsListFiltersQuery({
    programType: programType as ProgramType,
    includeOptions: false,
  });
  const useFilterById = usePriceSheetMappingsFilterById(programType as ProgramType);
  const { data, total = 0 } = mappingResponse || { data: [], total: 0 };
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get('page');
  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || undefined;
  const [appliedFilters, setAppliedFilters] = useState<Array<any>>([]);

  useEffect(() => {
    const pageNum = currentPage ? Number(currentPage) : 1;
    const filtersStr = advancedFilters || JSON.stringify([]);

    setAppliedFilters(JSON.parse(filtersStr));

    trigger({
      pageNum,
      pageSize: PAGE_SIZE,
      advancedFilters: filtersStr,
      searchTerm,
      programType: programType as ProgramType,
    });
  }, [trigger, currentPage, advancedFilters, searchTerm, programType]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      const hasValues = filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length);
      if (hasValues) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const onPageChange = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onSearchChange = useCallback(
    (term: string) => {
      if (term && term !== '') {
        searchParams.set('search', term);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  return (
    <>
      <Box margin="0 lg 0 lg">
        <FilterBar
          filtersState={{
            searchTerm,
          }}
          handleSearchTerm={onSearchChange}
          placeholder="Search by org, state, utility"
        >
          <Box direction="row" style={{ flexGrow: 1 }}>
            {!isFiltersLoading && (
              <AddFilterButtonDropdown
                dataFilters={filters}
                appliedFilters={appliedFilters}
                onRemoveFilter={handleRemoveAdvancedFilter}
                onApplyFilters={handleApplyAdvancedFilters}
                onClearFilters={handleClearAdvancedFilters}
                lazyFetchOptions
                useFilterById={useFilterById}
              />
            )}
          </Box>
        </FilterBar>
        {isFetching || isLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={data || []}
            isLoading={isLoading || isFetching}
            isScrollable={{ x: true }}
            hasStickyHeader
          />
        )}
      </Box>

      {data?.length > 0 && (
        <Pagination
          activePage={Number(currentPage) || 1}
          arePagesVisible
          itemsPerPage={PAGE_SIZE}
          numberOfPagesDisplayed={3}
          onChange={onPageChange}
          totalItemsCount={total}
          isCompact
        />
      )}
    </>
  );
};
